import { type PropsWithChildren, useEffect, useState } from 'react';
import * as channelService from '@channel.io/channel-web-sdk-loader';
import { useRouter } from 'next/router';
import { useIsInExternalPlatform } from '@/shared/externalPlatform';
import { detectIsInLti } from '@/shared/lti/detection';
import { getApplicationSessionId, getBrowserSessionId, getDeviceId } from '@/utils';
import { useUser } from '../auth';
enum LoadStep {
  BeforeLoad,
  Load,
  Boot,
}
const MOBILE_CHANNEL_BUTTON_QUERY = '(max-width: 767px)';
const MOBILE_CHANNEL_BUTTON_BOTTOM = 'calc(env(safe-area-inset-bottom) + 96px)';
function syncMobileChannelButtonOffset() {
  const launcher = document.getElementById('ch-plugin-launcher');
  if (!(launcher instanceof HTMLElement)) return;
  const shouldOffset = window.matchMedia(MOBILE_CHANNEL_BUTTON_QUERY).matches;
  const currentBottom = launcher.style.getPropertyValue('bottom');
  if (shouldOffset) {
    if (currentBottom !== MOBILE_CHANNEL_BUTTON_BOTTOM) {
      launcher.style.setProperty('bottom', MOBILE_CHANNEL_BUTTON_BOTTOM, 'important');
    }
    return;
  }
  if (currentBottom === MOBILE_CHANNEL_BUTTON_BOTTOM) {
    launcher.style.removeProperty('bottom');
  }
}
function isDisabledPathname(pathname: string) {
  const DISABLED_PATHNAMES = ['/quiz/[id]', '/play/[entryMapHashId]', '/update-browser', '/policy', '/terms', '/build'];
  if (DISABLED_PATHNAMES.includes(pathname)) {
    return true;
  }
  if (pathname.startsWith('/admin')) {
    return true;
  }
  return false;
}
export const ChannelTalk = ({
  children
}: PropsWithChildren) => {
  const {
    isReady,
    pathname
  } = useRouter();
  const isInExternalPlatform = useIsInExternalPlatform();
  const shouldDisable = isDisabledPathname(pathname) || isInExternalPlatform;
  const [loadStatus, setIsLoaded] = useState<LoadStep>(LoadStep.BeforeLoad);
  const {
    isInitialized,
    user
  } = useUser();
  useEffect(() => {
    if (isReady) {
      // Synchronous LTI check: useIsInExternalPlatform can lag on first render
      // during hydration. Delegate to the shared detection helper so all callsites
      // stay consistent (see shared/lti/detection.ts).
      const isLtiFromClient = detectIsInLti();
      if (!shouldDisable && !isLtiFromClient && loadStatus === LoadStep.BeforeLoad) {
        channelService.loadScript();
        setIsLoaded(LoadStep.Load);
      }
    }
  }, [isReady, shouldDisable, loadStatus]);
  useEffect(() => {
    if (loadStatus === LoadStep.Load) {
      channelService.boot({
        pluginKey: '26103066-96bf-40fa-8e6c-fc7afba2378f',
        profile: {
          zepBrowserSessionId: getBrowserSessionId(),
          zepApplicationSessionId: getApplicationSessionId(),
          zepDeviceId: getDeviceId() ?? '',
          serviceName: 'QUIZ'
        }
      });
      setIsLoaded(LoadStep.Boot);
    }
  }, [loadStatus]);
  useEffect(() => {
    if (loadStatus !== LoadStep.Boot) return;
    syncMobileChannelButtonOffset();
    const observer = new MutationObserver(syncMobileChannelButtonOffset);
    observer.observe(document.body, {
      attributeFilter: ['style'],
      attributes: true,
      childList: true,
      subtree: true
    });
    window.addEventListener('resize', syncMobileChannelButtonOffset);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', syncMobileChannelButtonOffset);
    };
  }, [loadStatus]);
  useEffect(() => {
    if (loadStatus !== LoadStep.Boot || !isInitialized) return;
    if (user) {
      channelService.updateUser({
        profile: {
          zepIsLoggedIn: true,
          zepUsername: user.username,
          zepEmail: user.email
        }
      });
    } else {
      channelService.updateUser({
        profile: {
          zepIsLoggedIn: false
        }
      });
    }
  }, [loadStatus, isInitialized, user]);
  useEffect(() => {
    if (loadStatus === LoadStep.BeforeLoad) return;
    if (shouldDisable) {
      channelService.hideChannelButton();
    } else {
      channelService.showChannelButton();
    }
  }, [loadStatus, shouldDisable]);
  useEffect(() => {
    return () => {
      if (loadStatus === LoadStep.Boot) {
        channelService.shutdown();
      }
    };
  }, [loadStatus]);
  return children;
};